/* Custom Input Component to be used with Formik */
import React, { useMemo, useEffect, useRef } from 'react';
import styled from 'styled-components/macro';
import PropTypes from 'prop-types';
import {
  Text,
  FormField,
  FormErrorMessage,
  FormFieldInfo,
} from 'components/atoms';
import { modifiersType } from 'types'
import { applyStyleModifiers } from 'styled-components-modifiers';
import { useFieldError } from 'hooks'

// this spacing is used below the error message, this makes the margin feel more natural
// when the error happears and disappears
const InputSpacingFix = styled.div`
  height: ${props => props.theme.spacing.m};
  width: 100%;
`;

const FIELD_CUSTOM_MODIFIERS = {
  stretch: () => `
   width: 100%;
  `,
};


const FieldCustom = ({
  className,
  title,
  field, // { name, value, onChange, onBlur }
  form: {
    touched, errors, isValid, isSubmitting, setFieldTouched, submitCount
  }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
  info,
  maxLetters,
  ...props
}) => {
  // focus on error after submit
  const fieldRef = useRef(null);
  useEffect(() => {
    if (isValid || !isSubmitting) return;
    const firstErrorKey = Object.keys(errors)[0];

    if (!isValid && firstErrorKey === field.name && fieldRef.current) {
      fieldRef.current.focus();
      setFieldTouched(field.name, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting, isValid, errors]);

  const isError = useFieldError({
    touched, errors, field, submitCount
  })

  const modifiersField = useMemo(() => {
    if (isError) return 'error';
    return '';
  }, [isError]);

  const numberOfLetters = useMemo(
    // eslint-disable-next-line react/prop-types
    () => (field && field.value ? field.value.length : 0),
    [field]
  );

  const isOverMaxLetter = useMemo(() => {
    if (typeof maxLetters === 'number') return numberOfLetters > maxLetters;
    return false;
  }, [maxLetters, numberOfLetters]);

  const renderLettersError = useMemo(() => {
    const info = `${numberOfLetters}/${maxLetters}`;
    if (isOverMaxLetter) return <Text modifiers={['danger']}>{info}</Text>;
    return <Text modifiers={['light']}>{info}</Text>;
  }, [numberOfLetters, maxLetters, isOverMaxLetter]);

  const handleBlur = (args) => {
    if ('onBlur' in field && typeof field.onBlur === 'function') field.onBlur(args)
    if ('onBlur' in props && typeof props.onBlur === 'function') props.onBlur()
  }

  return (
    <div className={className}>
      <Top>
        {title && <Text modifiers={['base']}>{title}</Text>}
        {maxLetters && renderLettersError}
      </Top>
      <FieldWrapper>
        <FormField
          type="text"
          as="textarea"
          modifiers={modifiersField}
          innerRef={fieldRef}
          {...field}
          {...props}
          onBlur={handleBlur}
        />
      </FieldWrapper>
      {isError && <FormErrorMessage>{errors[field.name]}</FormErrorMessage>}
      {info && <FormFieldInfo>{info}</FormFieldInfo>}
      <InputSpacingFix />
    </div>
  );
};

FieldCustom.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string,
  field: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.any,
    onBlur: PropTypes.func
  }).isRequired,
  form: PropTypes.shape({
    touched: PropTypes.shape({}),
    errors: PropTypes.shape({}),
    isSubmitting: PropTypes.bool,
    isValid: PropTypes.bool,
    setFieldTouched: PropTypes.func,
  }).isRequired,
  info: PropTypes.string,
  maxLetters: PropTypes.number,
  onBlur: PropTypes.func,
  modifiers: modifiersType,
};

FieldCustom.defaultProps = {
  className: '',
  title: null,
  info: null,
  maxLetters: null,
  onBlur: () => { },
  modifiers: []
};

const Top = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const FieldWrapper = styled.div`
  display: flex;
  cursor: pointer;
`

export default styled(FieldCustom)`
  ${Text} {
    padding-bottom: ${props => props.theme.spacing.s};
  }
  textarea {
    line-height: ${props => props.theme.spacing.l};
  }
  ${applyStyleModifiers(FIELD_CUSTOM_MODIFIERS)};
`;
